/**
 * Pure (Angular-free) helpers for converting object keys between casing styles.
 *
 * Used by the global HTTP response flowinglyCaseConversionInterceptor so the rest of the app can rely on
 * responses always being camelCase, regardless of whether the backend endpoint
 * applied the [CamelCaseAction] attribute.
 *
 * Design constraints:
 * - Recurses through plain objects and arrays only.
 * - Treats strings as opaque values, so stringified JSON payloads (e.g. FlowSchema,
 *   CardTasks, TableSchema) are not parsed or recursively converted here. Callers
 *   that parse these payloads must convert the parsed object separately if needed.
 * - Preserves keys beginning with '$' verbatim (and does not recurse into their
 *   values). These are AngularJS-internal / special containers that must not be
 *   altered.
 * - Leaves non-plain objects (Date, Blob, File, etc.) untouched.
 * - Lowercases leading acronym runs so keys map to conventional camelCase
 *   (e.g. CCUsers -> ccUsers, IPAddress -> ipAddress, ID -> id).
 * - Idempotent: running it on already-camelCase data is a no-op.
 */

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  if (Array.isArray(value)) {
    return false;
  }
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

/**
 * Converts a single key to camelCase using the same rules the interceptor
 * applies to response bodies. Exported so consumers that look up values in an
 * interceptor-converted dictionary (e.g. the account/settings response, keyed
 * by backend setting names) can resolve the converted key without hardcoding
 * it. Keys with no leading capital run (e.g. 'auth0:ClientId') are returned
 * unchanged, matching the interceptor.
 */
export function toCamelCaseKey(key: string): string {
  if (key.length === 0) {
    return key;
  }

  const leadingCapitals = key.match(/^[A-Z]+/);
  if (leadingCapitals === null) {
    return key;
  }

  const capitalRun = leadingCapitals[0];
  if (capitalRun.length === 1) {
    return key.charAt(0).toLowerCase() + key.slice(1);
  }

  const isFollowedByWord = /[a-z]/.test(key.charAt(capitalRun.length));
  if (isFollowedByWord) {
    return (
      capitalRun.slice(0, -1).toLowerCase() + key.slice(capitalRun.length - 1)
    );
  }

  return capitalRun.toLowerCase() + key.slice(capitalRun.length);
}

/**
 * Recursively converts the keys of an object (or array of objects) to camelCase.
 * Returns a new value; the input is not mutated.
 */
export function toCamelCaseDeep<T = unknown>(input: T): T {
  if (Array.isArray(input)) {
    return input.map((item) => toCamelCaseDeep(item)) as unknown as T;
  }

  if (!isPlainObject(input)) {
    return input;
  }

  const result: Record<string, unknown> = {};
  for (const key of Object.keys(input)) {
    const value = input[key];

    if (key.charAt(0) === '$') {
      result[key] = value;
      continue;
    }

    result[toCamelCaseKey(key)] = toCamelCaseDeep(value);
  }

  return result as unknown as T;
}
